"use client";

import { useTranslations } from "next-intl";
import { useFormContext } from "react-hook-form";
import { formatUnits } from "viem";
import { Button, Field, Label } from "@/components/ui";
import RHFInput from "@/components/ui/hook-form/rhf-input";

/**
 * Amount input + max button — mirrors DAO election AmountSection.
 *
 * Form context inherited via useFormContext (parent StatementVoteModal provides FormProvider).
 * "حداکثر DNM" fills the RHF 'amount' field with formatUnits(maxAmount, 18).
 */

interface VoteFormValues {
  source: "wallet" | "stake" | undefined;
  amount: string;
}

interface AmountSectionProps {
  maxAmount: bigint;
  sourceSelected: boolean;
  isError: boolean;
}

export const AmountSection: React.FC<AmountSectionProps> = ({
  maxAmount,
  sourceSelected,
  isError,
}) => {
  const t = useTranslations("statement");
  // Destructure setValue from RHF context — required for max button handler.
  // Parent StatementVoteModal renders <FormProvider>.
  const { setValue } = useFormContext<VoteFormValues>();

  const handleMaxClick = () => {
    const formatted = formatUnits(maxAmount, 18);
    setValue("amount", formatted, { shouldValidate: true });
  };

  return (
    <Field className="flex flex-col gap-3 w-full">
      <Label>{t("vote.amountLabel")}</Label>
      {/* Input + max button on the same row. items-start keeps the button
          pinned to the top of the row even when RHFInput grows downward to
          render an inline validation error — without this, error rendering
          pushes the input up and visually centers it against the button. */}
      <div className="flex flex-row items-start gap-3 w-full">
        <RHFInput
          name="amount"
          type="number"
          inputMode="decimal"
          step="any"
          min={0}
          placeholder=""
          // Hide native number-input spinners (Webkit + Firefox) while keeping
          // the numeric keyboard on mobile + numeric validation on desktop.
          // text-end: number inputs align left in RTL — intentional. rtl-ok
          className="h-8 rounded-xs text-end px-2 flex-1 [-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:m-0 [&::-webkit-outer-spin-button]:m-0" // rtl-ok
          disabled={!sourceSelected || isError}
        />
        <Button
          type="button"
          variant="solid"
          colors="default"
          size="md"
          radius="xs"
          className="px-4 shrink-0"
          disabled={!sourceSelected || isError || maxAmount === 0n}
          onClick={handleMaxClick}
        >
          {t("vote.maxButton")}
        </Button>
      </div>
    </Field>
  );
};
